/**
 * Greek Climate Resilience Fee (Τέλος ανθεκτικότητας στην κλιματική κρίση)
 *
 * Replaced the older "Stay tax" (Φόρος Διαμονής) from Jan 2024 onwards
 * via Law 5073/2023 (art. 30), increased again by Law 5162/2024 (art. 24).
 * It is a per-night, per-accommodation charge added to every booking,
 * billed by the host, and remitted to the Greek tax authority quarterly.
 *
 * Rates below match the AADE declaration form (Δήλωση Απόδοσης Τέλους
 * Ανθεκτικότητας στην Κλιματική Κρίση) sections A–E, current for the
 * 2026 tax year. The form's section letters map to our category keys:
 *
 *   A. Hotels                                → hotel_1_2_star … hotel_5_star
 *   B. Licensed Ενοικιαζόμενα Δωμάτια        → furnished_rental
 *   Γ. Short-term rental properties          → str_room_apartment (Γ.1),
 *                                              str_house_small   (Γ.2 <80m²),
 *                                              str_house_large   (Γ.2 ≥80m²)
 *   Δ. Self-catered tourist villas           → self_catered_villa
 *   Ε. Self-catered tourist residences       → self_catered_residence_*
 *
 * Most room2let listings fall under section Γ (Airbnb-style short-term
 * rental). Section B / Δ / Ε require a specific tourism business
 * license, which the host should self-declare.
 *
 * The rate depends on:
 *   1. The accommodation category (see table above).
 *   2. The night's season — high (Apr 1 – Oct 31) or low (Nov 1 – Mar 31).
 *
 * Total fee for a stay = (high-season nights × high rate)
 *                      + (low-season nights × low rate).
 *
 * Rates are in minor units (EUR cents). Cross-check against the latest
 * AADE form before publishing — the law is updated most years.
 */

export const CLIMATE_RESILIENCE_CATEGORIES = [
  // Section Γ — Short-term rental (default for platform listings)
  "str_room_apartment",
  "str_house_small",
  "str_house_large",
  // Section B — Licensed Ενοικιαζόμενα Δωμάτια / Διαμερίσματα
  "furnished_rental",
  // Section Δ — Self-catered tourist villa (Τουριστική Επαυλη)
  "self_catered_villa",
  // Section Ε — Self-catered tourist residence (Τουριστική Κατοικία)
  "self_catered_residence_sm",
  "self_catered_residence_lg",
  // Section A — Hotels (uncommon on room2let but supported for completeness)
  "hotel_1_2_star",
  "hotel_3_star",
  "hotel_4_star",
  "hotel_5_star",
  // ── Legacy keys, kept so existing listings don't break — map to the
  //    new equivalents (str_apartment → str_room_apartment,
  //    str_villa_lg → str_house_large). Hidden from the dropdown.
  "str_apartment",
  "str_villa_lg",
] as const;

export type ClimateResilienceCategory = (typeof CLIMATE_RESILIENCE_CATEGORIES)[number];

type Rate = {
  /** High-season per-night fee in EUR cents. */
  high: number;
  /** Low-season per-night fee in EUR cents. */
  low: number;
  /** Short label for UI. */
  label: string;
  /** Longer description with the per-night euros and any size criteria. */
  description: string;
  /** Section letter on the AADE Δήλωση form — A, B, Γ, Δ, Ε. */
  formSection: "A" | "B" | "Γ" | "Δ" | "Ε";
  /** Set true to hide this option from the dropdown — for legacy keys
   *  that we still accept on read but no longer offer on write. */
  hidden?: boolean;
};

export const CLIMATE_RESILIENCE_RATES: Record<ClimateResilienceCategory, Rate> = {
  // ── Section Γ — Short-term rental (the room2let default) ────────────
  str_room_apartment: {
    high: 800,
    low: 200,
    label: "Short-term rental · room or apartment",
    description:
      "Apartment, studio, room or loft offered via short-term rental (AADE form Γ.1). €8/night Apr–Oct, €2/night Nov–Mar.",
    formSection: "Γ",
  },
  str_house_small: {
    high: 800,
    low: 200,
    label: "Short-term rental · single-family home < 80 m²",
    description:
      "Detached house / cottage / townhouse under 80 m² offered via short-term rental (AADE form Γ.2 <80m²). €8/night Apr–Oct, €2/night Nov–Mar.",
    formSection: "Γ",
  },
  str_house_large: {
    high: 1500,
    low: 400,
    label: "Short-term rental · single-family home ≥ 80 m²",
    description:
      "Detached house / villa 80 m² or larger offered via short-term rental (AADE form Γ.2 ≥80m²). €15/night Apr–Oct, €4/night Nov–Mar.",
    formSection: "Γ",
  },

  // ── Section B — Licensed Ενοικιαζόμενα Δωμάτια / Διαμερίσματα ──────
  furnished_rental: {
    high: 200,
    low: 50,
    label: "Licensed furnished rooms / apartments",
    description:
      "Tourism-licensed Ενοικιαζόμενα Επιπλωμένα Δωμάτια / Διαμερίσματα (AADE form B). €2/night Apr–Oct, €0.50/night Nov–Mar.",
    formSection: "B",
  },

  // ── Section Δ — Self-catered tourist villas ─────────────────────────
  self_catered_villa: {
    high: 1500,
    low: 400,
    label: "Self-catered tourist villa (Τουριστική Έπαυλη)",
    description:
      "Licensed self-catered Tourist Furnished Villa (AADE form Δ). €15/night Apr–Oct, €4/night Nov–Mar.",
    formSection: "Δ",
  },

  // ── Section Ε — Self-catered tourist residences ─────────────────────
  self_catered_residence_sm: {
    high: 800,
    low: 200,
    label: "Self-catered tourist residence < 80 m²",
    description:
      "Licensed self-catered Tourist Furnished Residence under 80 m² (AADE form Ε <80m²). €8/night Apr–Oct, €2/night Nov–Mar.",
    formSection: "Ε",
  },
  self_catered_residence_lg: {
    high: 1500,
    low: 400,
    label: "Self-catered tourist residence ≥ 80 m²",
    description:
      "Licensed self-catered Tourist Furnished Residence 80 m² or larger (AADE form Ε ≥80m²). €15/night Apr–Oct, €4/night Nov–Mar.",
    formSection: "Ε",
  },

  // ── Section A — Hotels ──────────────────────────────────────────────
  hotel_1_2_star: {
    high: 200,
    low: 50,
    label: "1–2★ hotel",
    description: "1- or 2-star licensed hotel (AADE form A). €2/night Apr–Oct, €0.50/night Nov–Mar.",
    formSection: "A",
  },
  hotel_3_star: {
    high: 500,
    low: 150,
    label: "3★ hotel",
    description: "3-star licensed hotel (AADE form A). €5/night Apr–Oct, €1.50/night Nov–Mar.",
    formSection: "A",
  },
  hotel_4_star: {
    high: 1000,
    low: 300,
    label: "4★ hotel",
    description: "4-star licensed hotel (AADE form A). €10/night Apr–Oct, €3/night Nov–Mar.",
    formSection: "A",
  },
  hotel_5_star: {
    high: 1500,
    low: 400,
    label: "5★ hotel",
    description: "5-star licensed hotel (AADE form A). €15/night Apr–Oct, €4/night Nov–Mar.",
    formSection: "A",
  },

  // ── Legacy keys (hidden) ────────────────────────────────────────────
  // Kept so any listing saved before the rates were corrected continues
  // to compute the right amount. The values mirror the new equivalents.
  str_apartment: {
    high: 800,
    low: 200,
    label: "Short-term rental · room or apartment",
    description: "Legacy alias for str_room_apartment.",
    formSection: "Γ",
    hidden: true,
  },
  str_villa_lg: {
    high: 1500,
    low: 400,
    label: "Short-term rental · single-family home ≥ 80 m²",
    description: "Legacy alias for str_house_large.",
    formSection: "Γ",
    hidden: true,
  },
};

/**
 * Whether a given calendar day falls in the Greek high-season window
 * (April 1 through October 31, inclusive). The Climate Resilience Fee
 * law uses calendar months — no DST adjustment, no per-region tweak.
 */
export function isHighSeason(d: Date): boolean {
  const m = d.getUTCMonth() + 1; // 1..12
  return m >= 4 && m <= 10;
}

/**
 * Suggest the most appropriate category from a property type + size.
 * Used to pre-fill the dropdown when a host first sets up tax for an
 * existing listing; the host can still override the choice.
 */
export function suggestCategory(opts: {
  propertyType?: string | null;
  roomType?: string | null;
  sizeSqm?: number | null;
}): ClimateResilienceCategory {
  // Hotels and licensed self-catered villas are out of scope for the
  // room2let MVP — we only auto-suggest the section Γ categories. Hosts
  // who operate other tourism business types can switch manually.
  const t = (opts.propertyType ?? "").toLowerCase();
  const isHouseShape = t === "villa" || t === "house" || t === "cottage" || t === "townhouse";
  if (isHouseShape) {
    return (opts.sizeSqm ?? 0) >= 80 ? "str_house_large" : "str_house_small";
  }
  return "str_room_apartment";
}

export type ClimateResilienceBreakdown = {
  /** Total fee for the stay in minor units (EUR cents). */
  total: number;
  /** Nights that fell into the Apr–Oct window. */
  highNights: number;
  /** Nights that fell into the Nov–Mar window. */
  lowNights: number;
  /** Per-night high-season rate that applied (cents). */
  highRate: number;
  /** Per-night low-season rate that applied (cents). */
  lowRate: number;
  /** Human label of the category that applied. */
  categoryLabel: string;
};

/**
 * Compute the Climate Resilience Fee for a booking. checkIn is the
 * first billed night, checkOut is the morning of departure (not billed).
 * Iterates day-by-day to handle stays that straddle the season change
 * (e.g. Oct 28 → Nov 4 = 3 high + 4 low nights).
 */
export function computeClimateResilienceFee(
  category: ClimateResilienceCategory,
  checkIn: Date | string,
  checkOut: Date | string,
): ClimateResilienceBreakdown {
  const start = new Date(typeof checkIn === "string" ? checkIn : checkIn);
  const end = new Date(typeof checkOut === "string" ? checkOut : checkOut);
  const rates = CLIMATE_RESILIENCE_RATES[category];

  // Normalize to UTC midnight so DST doesn't shift the night count.
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));

  let total = 0;
  let highNights = 0;
  let lowNights = 0;

  // Safety cap — protects against a runaway loop if dates are inverted.
  const maxIter = 366;
  let i = 0;
  while (cursor < endDay && i < maxIter) {
    if (isHighSeason(cursor)) {
      total += rates.high;
      highNights += 1;
    } else {
      total += rates.low;
      lowNights += 1;
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    i += 1;
  }

  return {
    total,
    highNights,
    lowNights,
    highRate: rates.high,
    lowRate: rates.low,
    categoryLabel: rates.label,
  };
}

/**
 * Format the per-night rates for display, e.g. "€1.50 / €0.50".
 */
export function formatRateRange(category: ClimateResilienceCategory): string {
  const r = CLIMATE_RESILIENCE_RATES[category];
  return `€${(r.high / 100).toFixed(2)} / €${(r.low / 100).toFixed(2)}`;
}

/**
 * Map legacy category keys to their current equivalents. Existing
 * listings that were saved with the pre-2026 names still need to
 * resolve to a current dropdown option so the UI doesn't lose the
 * selection when the page re-renders.
 *
 * Returns null if the input is null/blank/unrecognised.
 */
export function normalizeCategory(
  c: string | null | undefined,
): ClimateResilienceCategory | null {
  if (!c) return null;
  // Legacy → current
  if (c === "str_apartment") return "str_room_apartment";
  if (c === "str_villa_lg") return "str_house_large";
  if ((CLIMATE_RESILIENCE_CATEGORIES as readonly string[]).includes(c)) {
    return c as ClimateResilienceCategory;
  }
  return null;
}
